import { useState, useEffect, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    TextField,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Divider,
    Snackbar
} from "@mui/material";
import {
    Edit,
    Check,
    Close,
    Add,
    Refresh,
    ContentCopy,
    Download,
    Upload
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
    getGetTaskVariablesIgnoringTenantQueryOptions,
    useUpdateProcessInstanceVariables,
    getGetProcessInstanceByIdIgnoringTenantQueryOptions
} from "../../../../../api/bpm/endpoints";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { PROCESSHISTORY, EDIT_PROCESS_VARIABLES } from "../../../../../constants/modules";

export const Route = createFileRoute('/__auth/processes/$processInstanceId/history/variables')({
    component: VariablesPage,
    staticData: {
        title: "routes.processVariables",
    },
});

function VariablesPage() {
    const { processInstanceId } = Route.useParams();
    const { t } = useTranslation();
    const { hasPermission, isAdmin } = usePermissions();

    const { data: processInstanceResponse } = useQuery({
        ...getGetProcessInstanceByIdIgnoringTenantQueryOptions(processInstanceId),
        enabled: !!processInstanceId
    });
    const processInstance = (processInstanceResponse as any)?.data || processInstanceResponse;
    const isProcessActive = processInstance ? (processInstance.state?.toUpperCase() === "ATIVO" || processInstance.state?.toUpperCase() === "ACTIVE") : true;
    const canEditVariables = isAdmin || hasPermission(`${PROCESSHISTORY}${EDIT_PROCESS_VARIABLES}`);

    const { data: processVariablesResponse, isLoading, error, refetch, isFetching } = useQuery({
        ...getGetTaskVariablesIgnoringTenantQueryOptions(
            { processInstanceIds: [processInstanceId] },
            {
                query: {
                    enabled: !!processInstanceId
                }
            }
        )
    });

    const updateVariablesMutation = useUpdateProcessInstanceVariables();

    const [localVariables, setLocalVariables] = useState<Array<{ name: string; type: string; value: string }>>([]);
    const [hasInitialized, setHasInitialized] = useState(false);

    const [editingVariables, setEditingVariables] = useState<Record<string, boolean>>({});
    const pendingEditsRef = useRef<Record<string, string>>({});

    const [isAddOpen, setIsAddOpen] = useState(false);

    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({
        open: false,
        message: "",
        severity: "success"
    });

    useEffect(() => {
        if (processVariablesResponse) {
            const vars = (processVariablesResponse as any)?.data?.[processInstanceId] || (processVariablesResponse as any)?.[processInstanceId] || {};
            const mapped = Object.entries(vars).map(([name, detail]: [string, any]) => ({
                name,
                type: detail.type || "String",
                value: detail.value !== undefined ? (typeof detail.value === 'object' ? JSON.stringify(detail.value) : String(detail.value)) : ""
            }));
            setLocalVariables(mapped);
            setHasInitialized(true);
        }
    }, [processVariablesResponse, processInstanceId]);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">{t("processes.history.errorLoadingVariables")}</Alert>
            </Box>
        );
    }

    const handleEditClick = (variable: { name: string; value: string }) => {
        setEditingVariables(prev => ({
            ...prev,
            [variable.name]: true
        }));
        pendingEditsRef.current[variable.name] = variable.value;
    };

    const handleSaveClick = (name: string) => {
        const valueToSave = pendingEditsRef.current[name];
        if (valueToSave === undefined) return;

        updateVariablesMutation.mutate({
            processInstanceId,
            data: {
                variables: {
                    [name]: String(valueToSave) as any
                }
            }
        }, {
            onSuccess: () => {
                setLocalVariables(prev => prev.map(v => v.name === name ? { ...v, value: valueToSave } : v));
                setEditingVariables(prev => {
                    const next = { ...prev };
                    delete next[name];
                    return next;
                });
                delete pendingEditsRef.current[name];
                refetch();
            },
            onError: () => {
                setSnackbar({
                    open: true,
                    message: t("processes.history.errorUpdatingVariables"),
                    severity: "error"
                });
            }
        });
    };

    const handleCancelClick = (name: string) => {
        setEditingVariables(prev => {
            const next = { ...prev };
            delete next[name];
            return next;
        });
        delete pendingEditsRef.current[name];
    };

    const handleSaveAll = () => {
        const variablesObj: Record<string, any> = {};
        Object.keys(editingVariables).forEach((name) => {
            variablesObj[name] = String(pendingEditsRef.current[name] ?? "");
        });

        updateVariablesMutation.mutate({
            processInstanceId,
            data: {
                variables: variablesObj as any
            }
        }, {
            onSuccess: () => {
                setLocalVariables(prev => prev.map(v => {
                    if (editingVariables[v.name]) {
                        return { ...v, value: pendingEditsRef.current[v.name] ?? "" };
                    }
                    return v;
                }));
                setEditingVariables({});
                pendingEditsRef.current = {};
                refetch();
            },
            onError: () => {
                setSnackbar({
                    open: true,
                    message: t("processes.history.errorUpdatingVariables"),
                    severity: "error"
                });
            }
        });
    };

    const handleCancelAll = () => {
        setEditingVariables({});
        pendingEditsRef.current = {};
    };

    const handleValueChange = (name: string, value: string) => {
        pendingEditsRef.current[name] = value;
    };

    const handleOpenAdd = () => {
        setIsAddOpen(true);
    };

    const handleCloseAdd = () => {
        setIsAddOpen(false);
    };

    const handleSaveAdd = (newVariablesMap: Record<string, string>) => {
        updateVariablesMutation.mutate({
            processInstanceId,
            data: {
                variables: newVariablesMap as any
            }
        }, {
            onSuccess: () => {
                setLocalVariables(prev => {
                    let next = [...prev];
                    Object.entries(newVariablesMap).forEach(([name, value]) => {
                        const exists = next.some(v => v.name === name);
                        if (exists) {
                            next = next.map(v => v.name === name ? { ...v, value } : v);
                        } else {
                            next.push({ name, type: "String", value });
                        }
                    });
                    return next;
                });
                setIsAddOpen(false);
                refetch();
            },
            onError: () => {
                setSnackbar({
                    open: true,
                    message: t("processes.history.errorUpdatingVariables"),
                    severity: "error"
                });
            }
        });
    };

    const handleCopyClick = (value: string) => {
        navigator.clipboard.writeText(value).then(() => {
            setSnackbar({
                open: true,
                message: t("processes.history.copySuccess"),
                severity: "success"
            });
        });
    };

    const handleDownloadJson = () => {
        const variablesObj: Record<string, string> = {};
        localVariables.forEach((v) => {
            variablesObj[v.name] = String(v.value);
        });

        const exportData = {
            variables: variablesObj
        };

        const blob = new Blob([JSON.stringify(exportData, null, 4)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `process-${processInstanceId}-variables.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const hasPendingEdits = Object.keys(editingVariables).length > 0;
    const isMutationPending = updateVariablesMutation.isPending || (updateVariablesMutation as any).isLoading;

    return (
        <Box sx={{ mt: 2 }}>
            {!isProcessActive ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {t("processes.history.onlyActiveEditable")}
                </Alert>
            ) : !canEditVariables ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {t("processes.history.noPermissionToEdit")}
                </Alert>
            ) : null}

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>{t("processes.history.variablesTitle")}</Typography>
                <Stack direction="row" spacing={1}>
                    {hasPendingEdits && (
                        <>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Check />}
                                onClick={handleSaveAll}
                                disabled={isMutationPending || !isProcessActive || !canEditVariables}
                            >
                                {t("processes.history.saveAll")}
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<Close />}
                                onClick={handleCancelAll}
                                disabled={isMutationPending}
                            >
                                {t("processes.history.cancelAll")}
                            </Button>
                        </>
                    )}
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<Refresh />}
                        onClick={() => refetch()}
                        disabled={isFetching || isMutationPending}
                    >
                        {t("processes.history.reload")}
                    </Button>
                    {isAdmin && (
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<Download />}
                            onClick={handleDownloadJson}
                            disabled={isFetching || isMutationPending}
                        >
                            {t("processes.history.downloadVariables") || "Baixar JSON"}
                        </Button>
                    )}
                    {isProcessActive && canEditVariables && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Add />}
                            onClick={handleOpenAdd}
                            disabled={isMutationPending}
                        >
                            {t("processes.history.addVariable")}
                        </Button>
                    )}
                </Stack>
            </Stack>

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack spacing={0} divider={<Divider />}>
                    {localVariables.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                            {t("processes.history.noVariablesFound")}
                        </Typography>
                    ) : (
                        localVariables.map((variable) => (
                            <VariableRow
                                key={variable.name}
                                variable={variable}
                                isEditing={!!editingVariables[variable.name]}
                                isProcessActive={isProcessActive}
                                canEditVariables={canEditVariables}
                                isMutationPending={isMutationPending}
                                onEditClick={handleEditClick}
                                onSaveClick={handleSaveClick}
                                onCancelClick={handleCancelClick}
                                onCopyClick={handleCopyClick}
                                onValueChange={handleValueChange}
                            />
                        ))
                    )}
                </Stack>
            </Paper>

            <AddVariablesDialog
                open={isAddOpen}
                onClose={handleCloseAdd}
                onSave={handleSaveAdd}
                isSaving={isMutationPending}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

interface VariableRowProps {
    variable: { name: string; value: string; type: string };
    isEditing: boolean;
    isProcessActive: boolean;
    canEditVariables: boolean;
    isMutationPending: boolean;
    onEditClick: (variable: { name: string; value: string }) => void;
    onSaveClick: (name: string) => void;
    onCancelClick: (name: string) => void;
    onCopyClick: (value: string) => void;
    onValueChange: (name: string, value: string) => void;
}

function VariableRow({
    variable,
    isEditing,
    isProcessActive,
    canEditVariables,
    isMutationPending,
    onEditClick,
    onSaveClick,
    onCancelClick,
    onCopyClick,
    onValueChange
}: VariableRowProps) {
    const { t } = useTranslation();
    const [localValue, setLocalValue] = useState(variable.value);

    useEffect(() => {
        if (!isEditing) {
            setLocalValue(variable.value);
        }
    }, [isEditing, variable.value]);

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setLocalValue(val);
        onValueChange(variable.name, val);
    };

    return (
        <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: 220, flexShrink: 0 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600, wordBreak: 'break-word' }}>
                    {variable.name}
                </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                {isEditing ? (
                    <TextField
                        size="small"
                        fullWidth
                        value={localValue}
                        onChange={handleTextChange}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") {
                                onCancelClick(variable.name);
                            } else if (e.key === "Enter") {
                                onSaveClick(variable.name);
                            }
                        }}
                        autoFocus
                        disabled={isMutationPending || !isProcessActive || !canEditVariables}
                    />
                ) : (
                    <Typography sx={{ wordBreak: 'break-all', fontSize: '0.875rem' }}>
                        {variable.value}
                    </Typography>
                )}
            </Box>

            <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: 0.5, minWidth: 80 }}>
                {isEditing ? (
                    <Stack direction="row" spacing={0.5}>
                        <IconButton
                            color="success"
                            size="small"
                            onClick={() => onSaveClick(variable.name)}
                            disabled={isMutationPending || !isProcessActive || !canEditVariables}
                        >
                            <Check fontSize="small" />
                        </IconButton>
                        <IconButton
                            color="error"
                            size="small"
                            onClick={() => onCancelClick(variable.name)}
                            disabled={isMutationPending}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </Stack>
                ) : (
                    <Stack direction="row" spacing={0.5}>
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={() => onCopyClick(variable.value)}
                            title={t("processes.history.copyTooltip")}
                            disabled={isMutationPending}
                        >
                            <ContentCopy fontSize="small" />
                        </IconButton>
                        {isProcessActive && canEditVariables && (
                            <IconButton
                                color="primary"
                                size="small"
                                onClick={() => onEditClick(variable)}
                                disabled={isMutationPending}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                )}
            </Box>
        </Box>
    );
}

interface AddVariablesDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (variables: Record<string, string>) => void;
    isSaving: boolean;
}

function AddVariablesDialog({ open, onClose, onSave, isSaving }: AddVariablesDialogProps) {
    const { t } = useTranslation();
    const [newName, setNewName] = useState("");
    const [newValue, setNewValue] = useState("");
    const [stagedVariables, setStagedVariables] = useState<Array<{ name: string; value: string }>>([]);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
        open: false,
        message: "",
        severity: "error"
    });

    useEffect(() => {
        if (open) {
            setNewName("");
            setNewValue("");
            setStagedVariables([]);
            setSnackbar({ open: false, message: "", severity: "error" });
        }
    }, [open]);

    const handleAddStaged = () => {
        const trimmedName = newName.trim();
        if (!trimmedName) return;

        if (stagedVariables.some(v => v.name === trimmedName)) {
            return;
        }

        setStagedVariables(prev => [...prev, { name: trimmedName, value: newValue }]);
        setNewName("");
        setNewValue("");
    };

    const handleRemoveStaged = (index: number) => {
        setStagedVariables(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = () => {
        const finalVariables: Record<string, string> = {};

        stagedVariables.forEach(v => {
            finalVariables[v.name] = v.value;
        });

        const trimmedName = newName.trim();
        if (trimmedName) {
            finalVariables[trimmedName] = newValue;
        }

        if (Object.keys(finalVariables).length === 0) return;

        onSave(finalVariables);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const json = JSON.parse(event.target?.result as string);
                if (json && typeof json === "object" && json.variables && typeof json.variables === "object") {
                    const imported: Array<{ name: string; value: string }> = [];
                    Object.entries(json.variables).forEach(([name, value]) => {
                        imported.push({ name, value: String(value) });
                    });
                    
                    if (imported.length > 0) {
                        setStagedVariables(prev => {
                            const existingNames = new Set(prev.map(v => v.name));
                            const filteredImported = imported.filter(v => !existingNames.has(v.name));
                            return [...prev, ...filteredImported];
                        });
                    }
                } else {
                    setSnackbar({
                        open: true,
                        message: t("processes.history.invalidImportFormat"),
                        severity: "error"
                    });
                }
            } catch (err) {
                setSnackbar({
                    open: true,
                    message: t("processes.history.invalidJsonFile"),
                    severity: "error"
                });
            }
        };
        reader.readAsText(file);
        e.target.value = "";
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{t("processes.history.addVariable")}</span>
                <Button
                    variant="outlined"
                    component="label"
                    startIcon={<Upload />}
                    disabled={isSaving}
                    size="small"
                >
                    {t("processes.history.importVariables")}
                    <input
                        type="file"
                        accept=".json"
                        hidden
                        onChange={handleFileChange}
                    />
                </Button>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {stagedVariables.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                {t("processes.history.variablesStaged")}
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 1, maxHeight: 150, overflowY: 'auto' }}>
                                <Stack spacing={1}>
                                    {stagedVariables.map((v, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                bgcolor: 'action.hover',
                                                p: 1,
                                                borderRadius: 1
                                            }}
                                        >
                                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', pr: 2 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {v.name}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', wordBreak: 'break-all' }}>
                                                    {v.value}
                                                </Typography>
                                            </Box>
                                            <IconButton size="small" color="error" onClick={() => handleRemoveStaged(index)}>
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>
                        </Box>
                    )}

                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField
                            label={t("common.name")}
                            fullWidth
                            size="small"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            disabled={isSaving}
                        />
                        <TextField
                            label={t("processes.history.value")}
                            fullWidth
                            size="small"
                            value={newValue}
                            onChange={(e) => setNewValue(e.target.value)}
                            disabled={isSaving}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && newName.trim()) {
                                    e.preventDefault();
                                    handleAddStaged();
                                }
                            }}
                        />
                        <IconButton
                            color="primary"
                            onClick={handleAddStaged}
                            disabled={!newName.trim() || isSaving}
                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, height: 40, width: 40 }}
                        >
                            <Add />
                        </IconButton>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="outlined" disabled={isSaving}>
                    {t("common.cancel")}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={isSaving || (stagedVariables.length === 0 && !newName.trim())}
                >
                    {t("common.save")}
                </Button>
            </DialogActions>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}
